import { useMemo, useCallback, useEffect } from 'react';
import { Select } from '@iq/react-components';

const renderEmptyMessage = (messageText) => <div>{messageText}</div>;

const ModelPickerField = (properties) => {
  const {
    disabled,
    readonly,
    required = true,
    formData: value,
    formContext,
    schema: {
      dependsOnFormField,
      components = [],
      default: defaultValue,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      label,
      styles = {},
    },
    onChange,
    ...otherProperties
  } = properties;

  const selectedComponentId = formContext?.[dependsOnFormField];

  const selectedComponent = components.find((component) => component.id === selectedComponentId);

  const modelOptions = useMemo(() => {
    if (!selectedComponent?.models) return [];
    return selectedComponent.models.map((model) => ({
      value: model.modelId,
      label: model.customName || model.modelId,
    }));
  }, [selectedComponent]);

  useEffect(() => {
    if (value) {
      if (!selectedComponentId) {
        onChange(null);
        return;
      }
      if (selectedComponent) {
        const hasMatchingModel = selectedComponent.models?.some((model) => model.modelId === value);
        if (!hasMatchingModel) {
          onChange(null);
        }
      }
    }
  }, [selectedComponentId, selectedComponent, value, onChange]);

  const selectedModelOption = useMemo(() => {
    const targetModelId = value ?? defaultValue;
    if (targetModelId) {
      return modelOptions.find((option) => option.value === targetModelId) || null;
    }
    return null;
  }, [value, defaultValue, modelOptions]);

  const handleModelChange = useCallback(
    (selectedOption) => {
      onChange(selectedOption?.value ?? null);
    },
    [onChange]
  );

  const overrideStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, lineHeight: 'normal', ...styles.container }),
      singleValue: (base) => ({ ...base, lineHeight: 'normal', ...styles.singleValue }),
      control: (base) => ({ ...base, lineHeight: 'normal', ...styles.control }),
    }),
    [styles]
  );

  const isDisabled = disabled || !selectedComponentId || modelOptions.length === 0;

  const LabelComponent = ({ showRequired = true }) =>
    label && (
      <label className="control-label">
        {label}
        {required && showRequired && <span className="required">*</span>}
      </label>
    );

  return (
    <div className="model-picker-field">
      <LabelComponent />
      <Select
        {...otherProperties}
        className="schema-select-widget"
        isMulti={false}
        isClearable={isClearable && !isDisabled}
        closeMenuOnSelect={true}
        isDisabled={isDisabled}
        readOnly={readonly || isDisabled}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={selectedModelOption}
        onChange={isDisabled ? undefined : handleModelChange}
        options={modelOptions}
        noOptionsMessage={() => renderEmptyMessage(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </div>
  );
};

export default ModelPickerField;
